import {
  FloraButton,
  Modal,
  Flex,
  ModalCloseButton,
  ModalHeader,
  ModalTrigger,
  Toaster,
} from '@grupoboticario/flora-react';
import slugify from 'slugify';

import { FloraProvider } from 'containers/ProviderComposer/providers/FloraProvider';
import { ListToShare } from 'domains/images-to-share';
import { isMobile as isMobileHelper } from 'helpers';
import { useTracking } from 'hooks';

import Styles from './ModalShare.styles';
import { CarouselToShareProps } from '../CarouselToShare/CarouselToShare.d';

export const ModalShare = ({
  children,
  data,
  indice,
  imageItem,
}: {
  children: JSX.Element;
  data: CarouselToShareProps;
  indice: number;
  imageItem: ListToShare;
}) => {
  const isMobile = isMobileHelper();
  const { sendDataToGTM } = useTracking();

  const trackShare = (success: 'success' | 'error', method: 'baixar' | 'compartilhar') => {
    sendDataToGTM({
      event: 'select_promotion',
      ecommerce: {
        creative_name: imageItem?.image?.url,
        creative_slot: indice,
        promotion_id: slugify(
          `${data.title}-${imageItem.image.alt || imageItem?.description || ''}-${indice}`,
          {
            lower: true,
          }
        ),
        promotion_name: slugify(
          imageItem?.image?.alt || imageItem?.description || data.title || '',
          { lower: true }
        ),
      },
      event_parameters: {
        cd_interaction_detail: `click:${success}-${method}-imagem`,
        cd_section: slugify(`${data.title || ''}`, { lower: true }),
      },
    });
  };

  const shareImage = async () => {
    const image = await fetch(children.props.src);
    const blob = await image.blob();
    const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
    const imageData = { files: [file] };

    if (!navigator.canShare(imageData)) {
      Toaster.notify({
        kind: 'error',
        description: 'Infelizmente não foi possível compartilhar a imagem neste dispositivo.',
        origin: 'right-top',
      });
      return trackShare('error', 'compartilhar');
    }

    return navigator.share(imageData).then(() => {
      trackShare('success', 'compartilhar');
    });
  };

  const handleTrackDownloadImage = () => {
    trackShare('success', 'baixar');
  };

  const closeModal = () => {
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_img_divulgacao',
        cd_interaction_detail: 'click:fechar',
        cd_section: slugify(`${data.title || ''}`, { lower: true }),
      },
    });
  };

  return (
    <Modal>
      <ModalTrigger>{children}</ModalTrigger>
      <Styles.ModalContent size='large' isFull={true}>
        <ModalHeader>
          <FloraProvider>
            <ModalCloseButton
              onClick={closeModal}
              aria-label={`fechar apresentação com 
imagens de divulgação de 
${data.title} ampliadas`}
            />
          </FloraProvider>
        </ModalHeader>
        <Styles.ModalBody>
          <Styles.ModalItemWrapper align='center' justify='center'>
            <Styles.ModalImage
              src={children.props.src}
              data-testid='modal-image'
              aria-label={children.props.alt || children.props.title || data.description}
            />
          </Styles.ModalItemWrapper>
        </Styles.ModalBody>
        <Styles.ModalFooter>
          <FloraProvider>
            <Flex align='center' justify='center' gap='$2'>
              <FloraButton
                as='a'
                href={children.props.src}
                onClick={handleTrackDownloadImage}
                data-testid='download-button'
                download
                aria-label='baixar imagem'
                role='button'
              >
                Baixar Imagem
              </FloraButton>

              {isMobile && (
                <FloraButton
                  hierarchy='tertiary'
                  onClick={shareImage}
                  data-testid='share-button'
                  aria-label='compartilhar imagem'
                  role='button'
                >
                  Compartilhar
                </FloraButton>
              )}
            </Flex>
          </FloraProvider>
        </Styles.ModalFooter>
      </Styles.ModalContent>
    </Modal>
  );
};
